import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getColaSap, confirmarTareaSap, rechazarTareaSap, reabrirTareaSap, devolverTareaSap, getOtConfig, leerOfPdf } from '../api/client'
import ConfirmarOf from '../components/ot/ConfirmarOf'
import PageHeader from '../components/PageHeader'
import { diasDesde, repartirPdfs } from '../components/ot/otLogica'
import { formatFecha } from '../components/unidades/tipos'

const VISTAS = ['abiertas', 'confirmada', 'rechazada', 'todas']

// A task that takes the OF's PDF right now (a drop target).
const esperaPdf = (i) => i.tarea.transaccion === 'crear_liberar_of' && i.puede_ejecutar
const esPdf = (f) => !!f && (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))
const conArchivos = (e) => Array.from(e.dataTransfer?.types || []).includes('Files')
const TONO = {
  pendiente: 'border-l-amber-400 bg-amber-50/50',
  bloqueada: 'border-l-gray-300 bg-gray-50',
  confirmada: 'border-l-emerald-500 bg-emerald-50/50',
  rechazada: 'border-l-rose-500 bg-rose-50/60',
  anulada: 'border-l-gray-200 bg-white opacity-60',
}
const PILL = {
  pendiente: 'bg-amber-100 text-amber-800', bloqueada: 'bg-gray-200 text-gray-600',
  confirmada: 'bg-emerald-100 text-emerald-800', rechazada: 'bg-rose-100 text-rose-700',
  anulada: 'bg-gray-100 text-gray-500',
}

// Operaciones > Planta > Cola SAP: every transaction REM-PMO asks of SAP B1,
// oldest first, for whoever holds "ejecutar_cola_sap" (the warehouse, by
// hand, today). Do it in SAP with the data shown, then confirm it here --
// the OF with its folio and PDF, which releases the OT -- or reject it
// saying why.
export default function PlantaColaSap() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [vista, setVista] = useState('abiertas')
  const [items, setItems] = useState(null)
  const [puede, setPuede] = useState(false)
  const [error, setError] = useState('')
  const [arrastrando, setArrastrando] = useState(false)      // a file is being dragged over the page
  const [abiertos, setAbiertos] = useState({})               // { tareaId: file }: tasks whose confirm is open with a dropped PDF
  const [lote, setLote] = useState(null)                     // { leyendo, archivos: [{ file, coincide: [tareaId] }], ignorados }

  // Dragging a file from another window: light up the tasks waiting for an
  // OF PDF. A counter, because dragenter/leave fire for every child element.
  useEffect(() => {
    let n = 0
    const entra = (e) => { if (!conArchivos(e)) return; n++; setArrastrando(true) }
    const sale = (e) => { if (!conArchivos(e)) return; n = Math.max(0, n - 1); if (!n) setArrastrando(false) }
    const sobre = (e) => { if (conArchivos(e)) e.preventDefault() }
    const fin = (e) => { if (!conArchivos(e)) return; e.preventDefault(); n = 0; setArrastrando(false) }
    window.addEventListener('dragenter', entra)
    window.addEventListener('dragleave', sale)
    window.addEventListener('dragover', sobre)
    window.addEventListener('drop', fin)
    return () => {
      window.removeEventListener('dragenter', entra)
      window.removeEventListener('dragleave', sale)
      window.removeEventListener('dragover', sobre)
      window.removeEventListener('drop', fin)
    }
  }, [])

  const cargar = useCallback(() => {
    getColaSap(vista).then(xs => { setItems(xs); setError('') })
      .catch(err => setError(err.response?.status === 403 ? t('ot.cola.sinFuncion') : t('ot.cola.cargaFallo')))
  }, [vista, t])
  useEffect(() => { cargar() }, [cargar])
  useEffect(() => { getOtConfig().then(c => setPuede(c.funciones.includes('ejecutar_cola_sap'))).catch(() => {}) }, [])

  const destinos = (items || []).filter(esperaPdf)

  const codigoDe = (tareaId) => destinos.find(i => i.tarea.id === tareaId)?.ot_codigo || `#${tareaId}`
  const abrir = (tareaId, file) => setAbiertos(a => ({ ...a, [tareaId]: file }))
  const cerrar = (tareaId) => setAbiertos(a => { const b = { ...a }; delete b[tareaId]; return b })

  // Several PDFs at once (or one dropped outside a task): each is compared
  // with every OT waiting for its OF. A file that matches exactly one task,
  // which no other dropped file also matches, opens that task's confirm with
  // it (still to be confirmed by hand); the rest are listed to pick or drop
  // on the right task.
  const soltarVarios = async (lista) => {
    const pdfs = lista.filter(esPdf)
    const ignorados = lista.length - pdfs.length
    if (!pdfs.length) { setError(t('ot.cola.arrastre.noPdf')); return }
    setError('')
    setLote({ leyendo: true, archivos: pdfs.map(file => ({ file, coincide: [] })), ignorados })
    const archivos = await Promise.all(pdfs.map(async file => {
      const revs = await Promise.all(destinos.map(i => leerOfPdf(i.tarea.id, file)
        .then(r => (r?.coincide ? i.tarea.id : null)).catch(() => null)))
      return { file, coincide: revs.filter(Boolean) }
    }))
    setAbiertos(prev => repartirPdfs(archivos, prev))
    setLote({ leyendo: false, archivos, ignorados })
  }
  const soltarEnTarea = (tareaId, lista) => {
    const pdfs = lista.filter(esPdf)
    if (pdfs.length > 1) { soltarVarios(lista); return }  // several on one task: sort them all out
    if (!pdfs.length) { setError(t('ot.cola.arrastre.noPdf')); return }
    setError('')
    abrir(tareaId, pdfs[0])
  }
  // Where each dropped file is now: open on a task, or still to place.
  const abiertaEn = (file) => Object.entries(abiertos).find(([, f]) => f === file)?.[0]
  const candidatosDe = (tareaId) => (lote && !lote.leyendo
    ? lote.archivos.filter(a => a.coincide.includes(tareaId) && !abiertaEn(a.file)).map(a => a.file) : [])

  return (
    <div className="p-8" onDrop={e => { if (conArchivos(e)) soltarVarios(Array.from(e.dataTransfer.files || [])) }}>
      <div className="mb-6">
        <PageHeader title={t('ot.cola.titulo')} subtitle={t('ot.cola.subtitulo')} />
      </div>
      {arrastrando && (
        <div className="mb-4 rounded-xl border-2 border-dashed border-indigo-400 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          {destinos.length
            ? t('ot.cola.arrastre.guia', { count: destinos.length })
            : t('ot.cola.arrastre.sinDestinos')}
        </div>
      )}
      {lote && (
        <div className="mb-4 rounded-xl border border-indigo-200 bg-white px-4 py-3 text-sm">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-medium text-gray-900">{t('ot.cola.arrastre.lote', { count: lote.archivos.length })}</span>
            {lote.leyendo && <span className="text-gray-500">{t('ot.cola.arrastre.comparando', { count: destinos.length })}</span>}
            {lote.ignorados > 0 && <span className="text-xs text-amber-700">{t('ot.cola.arrastre.ignorados', { count: lote.ignorados })}</span>}
            <button type="button" onClick={() => setLote(null)} className="ml-auto text-xs text-gray-500 hover:text-gray-900">{t('ot.cola.arrastre.cerrar')}</button>
          </div>
          <ul className="space-y-1">
            {lote.archivos.map((a, n) => {
              const en = abiertaEn(a.file)
              return (
                <li key={`${a.file.name}-${n}`} className={`flex flex-wrap items-center gap-2 rounded-md px-2.5 py-1 ${
                  lote.leyendo ? 'bg-gray-50' : en ? 'bg-emerald-50' : a.coincide.length ? 'bg-indigo-50' : 'bg-amber-50'}`}>
                  <span className="text-gray-700 truncate max-w-[40ch]">📄 {a.file.name}</span>
                  {lote.leyendo ? <span className="text-xs text-gray-400">…</span>
                    : en ? <span className="text-xs text-emerald-800">✓ {t('ot.cola.arrastre.abiertaEn', { ot: codigoDe(Number(en)) })}</span>
                      : a.coincide.length ? (
                        <span className="text-xs text-indigo-900">{t('ot.cola.arrastre.coincideCon')}
                          {a.coincide.map(id => (
                            <button key={id} type="button" onClick={() => abrir(id, a.file)}
                              className="ml-1.5 font-mono font-semibold underline hover:no-underline">{codigoDe(id)}</button>
                          ))}
                        </span>
                      ) : <span className="text-xs text-amber-800">{t('ot.cola.arrastre.ninguna')}</span>}
                </li>
              )
            })}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          {VISTAS.map(v => (
            <button key={v} type="button" onClick={() => setVista(v)}
              className={`px-3 py-1 rounded-md text-sm font-medium ${vista === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
              {t(`ot.cola.vistas.${v}`)}
            </button>
          ))}
        </div>
        {items && <span className="text-xs text-gray-400">{t('ot.cola.nTareas', { count: items.length })}</span>}
        {!puede && items && <span className="text-xs text-gray-500 ml-auto">{t('ot.cola.soloLectura')}</span>}
      </div>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {!items ? (!error && <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p>)
        : !items.length ? <p className="text-sm text-gray-400 py-10 text-center">{t('ot.cola.vacia')}</p> : (
          <div className="space-y-3">
            {items.map(i => (
              <Tarea key={i.tarea.id} i={i} puede={puede} t={t} onCambio={() => { cerrar(i.tarea.id); cargar() }}
                onVer={() => navigate(`/operations/planta/ot?ot=${i.ot_id}`)}
                arrastrando={arrastrando} destino={esperaPdf(i)}
                candidatos={candidatosDe(i.tarea.id)}
                archivoSoltado={abiertos[i.tarea.id] || null}
                onSoltar={lista => soltarEnTarea(i.tarea.id, lista)}
                onUsar={file => abrir(i.tarea.id, file)}
                onCerrarArchivo={() => cerrar(i.tarea.id)} />
            ))}
          </div>
        )}
      <p className="text-[11px] text-gray-400 mt-4">{t('ot.cola.porValidar')}</p>
    </div>
  )
}

function Tarea({ i, puede, t, onCambio, onVer, arrastrando, destino, candidatos, archivoSoltado, onSoltar, onUsar, onCerrarArchivo }) {
  const x = i.tarea
  const [modoPropio, setModo] = useState(null)    // null | 'confirmar' | 'rechazar'
  // A PDF dropped on this task (or matched from a page drop) opens its confirm.
  const modo = archivoSoltado ? 'confirmar' : modoPropio
  const [encima, setEncima] = useState(false)
  const [folio, setFolio] = useState('')
  const [nota, setNota] = useState('')
  const [archivo, setArchivo] = useState(null)
  const [motivo, setMotivo] = useState('')
  const [devolver, setDevolver] = useState(true)
  const [verDatos, setVerDatos] = useState(false)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const esOf = x.transaccion === 'crear_liberar_of'

  const correr = async (fn) => {
    setOcupado(true); setError('')
    try { await fn(); setModo(null); onCambio() }
    catch (err) {
      const d = err.response?.data?.detail
      setError(d?.code === 'validacion'
        ? t('ot.faltanDatos', { lista: d.faltan.map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ') })
        : d?.message || t('ot.panel.fallo'))
    }
    setOcupado(false)
  }
  // Tasks other than the OF: folio and document optional (the OF has ConfirmarOf).
  const confirmar = () => correr(() => confirmarTareaSap(x.id, { folio: folio.trim(), nota: nota.trim(), archivo }))

  return (
    <section
      onDragOver={e => { if (destino && conArchivos(e)) { e.preventDefault(); setEncima(true) } }}
      onDragLeave={() => setEncima(false)}
      onDrop={e => {
        if (!destino || !conArchivos(e)) return
        e.preventDefault(); e.stopPropagation(); setEncima(false)
        onSoltar(Array.from(e.dataTransfer.files || []))
      }}
      className={`relative rounded-xl border border-gray-200 border-l-4 px-4 py-3 transition-all ${TONO[x.estado] || 'bg-white'} ${
        arrastrando ? (destino ? `ring-2 ring-offset-2 ${encima ? 'ring-indigo-600 scale-[1.01] shadow-lg' : 'ring-indigo-300 ring-dashed'}` : 'opacity-40')
          : archivoSoltado || candidatos.length ? 'ring-2 ring-emerald-500' : ''}`}>
      {arrastrando && destino && (
        <div className="pointer-events-none absolute inset-0 rounded-xl flex items-center justify-center bg-indigo-50/80">
          <span className="text-sm font-semibold text-indigo-800">
            {encima ? t('ot.cola.arrastre.suelta') : t('ot.cola.arrastre.aqui', { ot: i.ot_codigo })}
          </span>
        </div>
      )}
      {destino && !archivoSoltado && candidatos.map((f, n) => (
        <div key={`${f.name}-${n}`} className="mb-2 flex items-center justify-between gap-2 rounded-md bg-emerald-100 px-2.5 py-1 text-xs text-emerald-900">
          <span className="truncate">{t('ot.cola.arrastre.esDeEsta', { archivo: f.name })}</span>
          <button type="button" onClick={() => onUsar(f)} className="shrink-0 font-semibold underline hover:no-underline">{t('ot.cola.arrastre.usar')}</button>
        </div>
      ))}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{t(`ot.transacciones.${x.transaccion}`, { defaultValue: x.transaccion })}</span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${PILL[x.estado]}`}>{t(`ot.estadosTarea.${x.estado}`)}</span>
            {i.prioridad && <span className="text-[11px] text-gray-500">· {t(`ot.prioridades.${i.prioridad}`)}</span>}
          </div>
          <p className="text-sm text-gray-700 mt-0.5">
            <button type="button" onClick={onVer} className="font-mono font-semibold text-gray-900 hover:underline">{i.ot_codigo}</button>
            {' · '}{i.articulo_descripcion || i.articulo_codigo}
            {i.articulo_codigo && i.articulo_descripcion && <span className="font-mono text-gray-500"> ({i.articulo_codigo})</span>}
            {i.cantidad != null && <> · × {i.cantidad}</>}
          </p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {[i.proyecto && (i.proyecto.code || i.proyecto.name), i.grupo?.nombre,
              i.fecha_compromiso && t('ot.cola.compromiso', { fecha: formatFecha(i.fecha_compromiso) }),
              t('ot.cola.enCola', { dias: t('ot.dias', { count: diasDesde(x.creada_en) }) })].filter(Boolean).join(' · ')}
          </p>
          {x.estado === 'bloqueada' && i.depende_de_transaccion && (
            <p className="text-xs text-gray-600 mt-1">⏸ {t('ot.panel.esperaA', { tarea: t(`ot.transacciones.${i.depende_de_transaccion}`) })}</p>
          )}
          {x.estado === 'rechazada' && x.motivo_rechazo && (
            <p className="text-xs text-rose-700 mt-1">{t('ot.cola.rechazadaPor', { motivo: x.motivo_rechazo })}</p>
          )}
          {x.folio_resultado && <p className="text-xs text-emerald-800 mt-1">{t('ot.panel.folio', { folio: x.folio_resultado })}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button type="button" onClick={() => setVerDatos(v => !v)}
            className="text-xs font-medium text-gray-600 border border-gray-200 bg-white rounded-md px-2.5 py-1 hover:bg-gray-50">
            {verDatos ? t('ot.cola.ocultarDatos') : t('ot.cola.verDatos')}
          </button>
          {i.puede_ejecutar && !modo && (
            <>
              <button type="button" onClick={() => setModo('rechazar')}
                className="text-xs font-medium text-gray-700 border border-gray-200 bg-white rounded-md px-2.5 py-1 hover:bg-gray-50">
                {t('ot.cola.rechazar')}
              </button>
              <button type="button" onClick={() => setModo('confirmar')}
                className="text-xs font-medium bg-gray-900 text-white rounded-md px-2.5 py-1 hover:bg-gray-800">
                {t('ot.cola.confirmar')}
              </button>
            </>
          )}
          {puede && x.estado === 'rechazada' && (i.ot_estado === 'planificada' || !['crear_bom', 'crear_liberar_of'].includes(x.transaccion)) && (
            <button type="button" disabled={ocupado} onClick={() => correr(() => reabrirTareaSap(x.id))}
              className="text-xs font-medium text-gray-700 border border-gray-200 bg-white rounded-md px-2.5 py-1 hover:bg-gray-50">
              {t('ot.cola.reabrir')}
            </button>
          )}
          {x.estado === 'rechazada' && i.puede_devolver && (
            <button type="button" disabled={ocupado}
              onClick={() => { if (window.confirm(t('ot.cola.devolverConfirm', { ot: i.ot_codigo }))) correr(() => devolverTareaSap(x.id)) }}
              className="text-xs font-medium bg-rose-600 text-white rounded-md px-2.5 py-1 hover:bg-rose-700 disabled:opacity-40">
              {t('ot.cola.devolverBoton')}
            </button>
          )}
        </div>
      </div>

      {verDatos && <DatosSap payload={x.payload} t={t} />}

      {modo === 'confirmar' && esOf && (
        <ConfirmarOf key={archivoSoltado ? `${archivoSoltado.name}-${archivoSoltado.size}` : 'manual'} tareaId={x.id} archivoInicial={archivoSoltado}
          onHecho={() => { setModo(null); onCambio() }}
          onCancelar={() => { setModo(null); setError(''); onCerrarArchivo() }} />
      )}
      {modo === 'confirmar' && !esOf && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 space-y-2">
          <p className="text-xs text-gray-600">{t('ot.cola.confirmarHint')}</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.cola.folio')}</label>
              <input className="input font-mono" value={folio} autoFocus onChange={e => setFolio(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.cola.documento')}</label>
              <input type="file" className="block w-full text-xs"
                onChange={e => setArchivo(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.cola.nota')}</label>
              <input className="input" value={nota} onChange={e => setNota(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setModo(null); setError('') }}
              className="text-xs font-medium text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-100">{t('common:actions.cancel')}</button>
            <button type="button" disabled={ocupado} onClick={confirmar}
              className="text-xs font-medium bg-emerald-600 text-white rounded-md px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-40">
              {t('ot.cola.confirmarHecha')}
            </button>
          </div>
        </div>
      )}
      {modo === 'rechazar' && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 space-y-2">
          <label className="block text-xs font-medium text-gray-700">{t('ot.cola.motivoRechazo')} *</label>
          <textarea rows={2} autoFocus className="input" value={motivo} onChange={e => setMotivo(e.target.value)} />
          {i.puede_devolver && (
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={devolver} onChange={e => setDevolver(e.target.checked)} />
              <span>
                <span className="font-medium">{t('ot.cola.devolver')}</span>
                <span className="block text-[11px] text-gray-500">{devolver ? t('ot.cola.devolverSi') : t('ot.cola.devolverNo')}</span>
              </span>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setModo(null); setError('') }}
              className="text-xs font-medium text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-100">{t('common:actions.cancel')}</button>
            <button type="button" disabled={ocupado || !motivo.trim()} onClick={() => correr(() => rechazarTareaSap(x.id, motivo.trim(), i.puede_devolver && devolver))}
              className="text-xs font-medium bg-rose-600 text-white rounded-md px-3 py-1.5 hover:bg-rose-700 disabled:opacity-40">
              {i.puede_devolver && devolver ? t('ot.cola.rechazarYDevolver') : t('ot.cola.rechazar')}
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </section>
  )
}

// The transaction's data, each value with a copy button (to paste in SAP).
function DatosSap({ payload, t }) {
  const [copiado, setCopiado] = useState(null)
  const filas = Object.entries(payload || {}).filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length))
  const copiar = (k, v) => {
    navigator.clipboard?.writeText(Array.isArray(v) ? v.join(', ') : String(v)).then(() => {
      setCopiado(k)
      setTimeout(() => setCopiado(c => (c === k ? null : c)), 1500)
    }).catch(() => {})
  }
  return (
    <dl className="mt-3 rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 text-sm">
      {filas.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3 px-3 py-1.5">
          <dt className="w-44 shrink-0 text-xs text-gray-500">{t(`ot.cola.campos.${k}`, { defaultValue: k })}</dt>
          <dd className="flex-1 min-w-0 font-mono text-gray-900 truncate">{Array.isArray(v) ? v.join(', ') : String(v)}</dd>
          <button type="button" onClick={() => copiar(k, v)}
            className="text-[11px] text-gray-500 hover:text-gray-900 shrink-0">{copiado === k ? t('ot.cola.copiado') : t('ot.cola.copiar')}</button>
        </div>
      ))}
    </dl>
  )
}
